import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase-client';

const registerWebPushSubscription = httpsCallable(functions, 'registerWebPushSubscription');
const unregisterWebPushSubscription = httpsCallable(functions, 'unregisterWebPushSubscription');
const updatePushPreferencesFn = httpsCallable(functions, 'updatePushPreferences');
const SERVICE_WORKER_READY_TIMEOUT_MS = 8000;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = `${base64String}${padding}`.replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function getBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch (err) {
    return 'UTC';
  }
}

export function isWebPushSupported() {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;
}

export function isPwaServiceWorkerEnabled() {
  return process.env.NODE_ENV === 'production';
}

export function getNotificationPermission() {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return window.Notification.permission || 'default';
}

export function getWebPushReadiness({ isIos = false, isStandalone = false } = {}) {
  const permission = getNotificationPermission();

  if (!isPwaServiceWorkerEnabled()) {
    return {
      canEnable: false,
      code: 'development-build',
      permission,
      message: 'Push notifications are available in deployed app builds.',
    };
  }

  if (!isStandalone) {
    return {
      canEnable: false,
      code: isIos ? 'ios-install-required' : 'install-required',
      permission,
      message: isIos
        ? 'On iPhone or iPad, install Member Nest to the Home Screen first. Then open the app from the Home Screen to enable notifications.'
        : 'Install Member Nest first, then open the app from your Home Screen to enable notifications.',
    };
  }

  if (!isWebPushSupported()) {
    return {
      canEnable: false,
      code: 'unsupported',
      permission,
      message: 'Push notifications are not supported on this device or browser.',
    };
  }

  if (!process.env.REACT_APP_WEB_PUSH_PUBLIC_VAPID_KEY) {
    return {
      canEnable: false,
      code: 'missing-vapid-key',
      permission,
      message: 'Push notifications are not configured yet.',
    };
  }

  if (permission === 'denied') {
    return {
      canEnable: false,
      code: 'permission-denied',
      permission,
      message: 'Notifications are blocked for Member Nest. Enable them in your device or browser settings.',
    };
  }

  return {
    canEnable: true,
    code: permission === 'granted' ? 'ready-granted' : 'ready',
    permission,
    message: permission === 'granted'
      ? 'Notifications are allowed on this device.'
      : 'Notifications can be enabled on this device.',
  };
}

function webPushError(message, code) {
  const error = new Error(message);
  error.code = code;
  return error;
}

function isPushServiceUnavailableError(error) {
  const name = String(error?.name || '').toLowerCase();
  const message = String(error?.message || '').toLowerCase();
  return name === 'aborterror'
    && (
      message.includes('push service not available')
      || message.includes('push service')
    );
}

function waitForServiceWorkerReady(timeoutMs = SERVICE_WORKER_READY_TIMEOUT_MS) {
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise((_, reject) => {
      window.setTimeout(() => reject(new Error('The app service worker is not ready yet. Close and reopen the installed app, then try again.')), timeoutMs);
    }),
  ]);
}

export async function getWebPushSubscriptionStatus({ isIos = false, isStandalone = false } = {}) {
  const readiness = getWebPushReadiness({ isIos, isStandalone });
  if (!readiness.canEnable) return { ...readiness, enabled: false };
  if (readiness.permission !== 'granted') return { ...readiness, enabled: false };

  try {
    const registration = await waitForServiceWorkerReady(2500);
    const subscription = await registration.pushManager.getSubscription();
    return {
      ...readiness,
      code: subscription ? 'enabled' : 'allowed-not-subscribed',
      enabled: Boolean(subscription),
      message: subscription
        ? 'Push notifications are enabled on this device.'
        : 'Notifications are allowed, but this device is not subscribed yet.',
    };
  } catch (err) {
    return {
      ...readiness,
      canEnable: false,
      code: 'service-worker-not-ready',
      enabled: false,
      message: err.message || 'The app service worker is not ready yet.',
    };
  }
}

export async function enableWebPush({ isIos = false, isStandalone = false } = {}) {
  const readiness = getWebPushReadiness({ isIos, isStandalone });
  if (!readiness.canEnable) {
    throw webPushError(readiness.message, readiness.code);
  }

  const vapidKey = process.env.REACT_APP_WEB_PUSH_PUBLIC_VAPID_KEY;
  const permission = await window.Notification.requestPermission();
  if (permission !== 'granted') {
    const blocked = permission === 'denied';
    throw webPushError(
      blocked
        ? 'Notifications are blocked for Member Nest. Enable them in your device or browser settings.'
        : 'Notification permission was not granted.',
      blocked ? 'permission-denied' : 'permission-not-granted'
    );
  }

  const registration = await waitForServiceWorkerReady();
  const existing = await registration.pushManager.getSubscription();
  let subscription = existing;
  if (!subscription) {
    try {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });
    } catch (err) {
      if (isPushServiceUnavailableError(err)) {
        throw webPushError(
          'Push notifications are not available on this device right now. Reopen the installed app or try again later.',
          'push-service-unavailable'
        );
      }
      throw err;
    }
  }

  await registerWebPushSubscription({
    subscription: subscription.toJSON(),
    timezone: getBrowserTimezone(),
    userAgent: navigator.userAgent || '',
  });

  return subscription;
}

export async function disableWebPush() {
  if (!isWebPushSupported()) {
    await unregisterWebPushSubscription({});
    return;
  }

  let registration = null;
  try {
    registration = await waitForServiceWorkerReady(2500);
  } catch (err) {
    await unregisterWebPushSubscription({});
    return;
  }
  const subscription = await registration.pushManager.getSubscription();
  if (subscription) {
    await unregisterWebPushSubscription({ endpoint: subscription.endpoint });
    await subscription.unsubscribe().catch(() => false);
  } else {
    await unregisterWebPushSubscription({});
  }
}

export async function updatePushPreferences(pushPreferences) {
  await updatePushPreferencesFn({
    pushPreferences: {
      ...pushPreferences,
      timezone: pushPreferences?.timezone || getBrowserTimezone(),
    },
  });
}
